import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../stores/authStore";
import styles from "./components/Pedidos/OrderDetailModal.module.css";
import awsStyles from "./AwsOrderDetailModal.module.css";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import AwsOrderPrintView from "./AwsOrderPrintView";

interface AwsOrderDetailModalProps {
  pedidoId: number;
  onClose: () => void;
  onEdit: (pedidoId: number) => void;
  onFinalize?: () => void;
}

const formatCurrency = (value: any) => {
  const num = Number(value);
  if (isNaN(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
};

const SIZES = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

const AwsOrderDetailModal: React.FC<AwsOrderDetailModalProps> = ({
  pedidoId,
  onClose,
  onEdit,
  onFinalize,
}) => {
  const { token } = useAuthStore();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"itens" | "obs">("itens");
  const [showPrint, setShowPrint] = useState(false);

  const handleFinalize = async () => {
    if (!window.confirm("Deseja realmente finalizar este pedido?")) return;
    try {
      setFinalizing(true);
      const apiURL = process.env.REACT_APP_API_URL || "";
      await axios.put(
        `${apiURL}/api/orders/aws/${pedidoId}/finalize`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      alert("Pedido finalizado com sucesso!");
      if (onFinalize) onFinalize();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Falha ao finalizar pedido.");
    } finally {
      setFinalizing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const apiURL = process.env.REACT_APP_API_URL || "";
        const res = await axios.get(`${apiURL}/api/orders/aws/${pedidoId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError("Falha ao carregar detalhe do pedido.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pedidoId, token]);

  if (loading)
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContainer}>
          <div className={styles.loadingContainer}>
            <LoadingSpinner message="Carregando..." />
          </div>
        </div>
      </div>
    );

  if (error || !data)
    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContainer}>
          <div className={styles.errorContainer}>
            {error || "Erro ao carregar."}
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
      </div>
    );

  const { pedido, produtos, totalItens, totalValor } = data;

  // Se estiver em modo de impressão, renderiza a view de impressão
  if (showPrint) {
    return (
      <AwsOrderPrintView
        pedidoId={pedidoId}
        onClose={() => setShowPrint(false)}
      />
    );
  }

  // Build a grade map per product: { produtoId: { tamanho: quantidade } }
  const getGradeMap = (grades: any[]) => {
    const map: Record<number, number> = {};
    for (const g of grades) map[g.tamanho] = g.quantidade;
    return map;
  };

  const statusLabel =
    ["Em Aberto", "Cancelado", "Finalizado", "Sincronizado"][pedido.status] ??
    "Desconhecido";

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <div className={`${styles.highlightSection} ${awsStyles.highlightFix}`}>
          <div className={awsStyles.headerInfo}>
            <h2 className={styles.clientName}>{pedido.cliente_nome}</h2>
            <p className={styles.orderId}>
              Pedido #{pedido.id} — {statusLabel} —{" "}
              {formatDate(pedido.data_emissao)}
            </p>
          </div>
          <div className={awsStyles.headerActions}>
            <button
              onClick={() => setShowPrint(true)}
              style={{
                backgroundColor: "#1B2A49",
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: "4px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "0.85rem",
              }}
            >
              📥 BAIXAR PDF
            </button>
            {pedido.status === 0 && (
              <>
                <button
                  onClick={() => {
                    onClose();
                    onEdit(pedidoId);
                  }}
                  style={{
                    backgroundColor: "#fff",
                    color: "#57171e",
                    border: "1px solid #57171e",
                    padding: "10px 16px",
                    borderRadius: "4px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  ✏️ EDITAR
                </button>
                <button
                  onClick={handleFinalize}
                  disabled={finalizing}
                  style={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    border: "none",
                    padding: "10px 16px",
                    borderRadius: "4px",
                    fontWeight: "bold",
                    cursor: finalizing ? "not-allowed" : "pointer",
                    opacity: finalizing ? 0.7 : 1,
                    fontSize: "0.85rem",
                  }}
                >
                  {finalizing ? "⌛..." : "🚀 FINALIZAR"}
                </button>
              </>
            )}
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            style={{ position: "static", marginLeft: "auto" }}
          >
            &times;
          </button>
        </div>

        <div className={`${styles.contentSection} ${awsStyles.contentFix}`}>
          <div className={styles.modalContent}>
            {/* Cabeçalho */}
            <div
              className={`${styles.formSection} ${styles.gridContainer} ${styles.headerGrid}`}
            >
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>SEGMENTO</label>
                <input
                  className={styles.formInput}
                  value={pedido.segmento_nome || ""}
                  readOnly
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>OPERAÇÃO</label>
                <input
                  className={styles.formInput}
                  value={pedido.operacao_fiscal || ""}
                  readOnly
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>COND. PAGAMENTO</label>
                <input
                  className={styles.formInput}
                  value={pedido.condicao_pgto || ""}
                  readOnly
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>CIDADE/UF</label>
                <input
                  className={styles.formInput}
                  value={`${pedido.cidade || ""} ${pedido.estado ? "- " + pedido.estado : ""}`}
                  readOnly
                />
              </div>
            </div>

            {/* Cliente retrátil */}
            {pedido.cliente_nome_full && (
              <div
                className={styles.formSection}
                style={{ paddingBottom: "10px" }}
              >
                <div className={`${styles.gridContainer} ${styles.clientGrid}`}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>CNPJ/CPF</label>
                    <input
                      className={styles.formInput}
                      value={pedido.DOCUMENTO || ""}
                      readOnly
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>ENDEREÇO</label>
                    <input
                      className={styles.formInput}
                      value={`${pedido.ENDERECO || ""} ${pedido.NUMERO ? ", " + pedido.NUMERO : ""}`}
                      readOnly
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>CIDADE</label>
                    <input
                      className={styles.formInput}
                      value={`${pedido.CIDADE || ""} - ${pedido.ESTADO || ""}`}
                      readOnly
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>TELEFONE</label>
                    <input
                      className={styles.formInput}
                      value={pedido.TELEFONE || ""}
                      readOnly
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>EMAIL</label>
                    <input
                      className={styles.formInput}
                      value={pedido.EMAIL || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Abas */}
            <div className={styles.formSection}>
              <div className={styles.tabHeader}>
                <button
                  className={`${styles.tabButton} ${activeTab === "itens" ? styles.tabButtonActive : ""}`}
                  onClick={() => setActiveTab("itens")}
                >
                  Itens ({produtos?.length || 0})
                </button>
                <button
                  className={`${styles.tabButton} ${activeTab === "obs" ? styles.tabButtonActive : ""}`}
                  onClick={() => setActiveTab("obs")}
                >
                  Observações
                </button>
              </div>

              {activeTab === "itens" && (
                <div className={styles.tabContent}>
                  <div className={awsStyles.tableWrap}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginTop: "10px",
                        fontSize: "0.82rem",
                        minWidth: "900px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#f8f9fa",
                            borderBottom: "2px solid #eee",
                          }}
                        >
                          <th style={{ padding: "8px", textAlign: "left" }}>
                            Ref.
                          </th>
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "left",
                              minWidth: "200px",
                            }}
                          >
                            Descrição
                          </th>
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "left",
                              minWidth: "80px",
                            }}
                          >
                            Opcional
                          </th>
                          <th style={{ padding: "8px", textAlign: "center" }}>
                            Tab.
                          </th>
                          <th style={{ padding: "8px", textAlign: "right" }}>
                            Preço
                          </th>
                          {SIZES.map((s) => (
                            <th
                              key={s}
                              style={{
                                padding: "4px",
                                textAlign: "center",
                                minWidth: "30px",
                                background: "#f1f3f5",
                              }}
                            >
                              {s}
                            </th>
                          ))}
                          <th style={{ padding: "8px", textAlign: "center" }}>
                            Qtd
                          </th>
                          <th style={{ padding: "8px", textAlign: "right" }}>
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {produtos && produtos.length > 0 ? (
                          produtos.map((prod: any, idx: number) => {
                            const gradeMap = getGradeMap(prod.grades || []);
                            const qtd = (prod.grades || []).reduce(
                              (s: number, g: any) => s + g.quantidade,
                              0,
                            );
                            return (
                              <tr
                                key={idx}
                                style={{
                                  borderBottom: "1px solid #f0f0f0",
                                  background:
                                    idx % 2 === 0 ? "#fff" : "#fafafa",
                                }}
                              >
                                <td
                                  style={{ padding: "8px", fontWeight: "bold" }}
                                >
                                  {prod.REFERENCIA}
                                </td>
                                <td style={{ padding: "8px" }}>
                                  {prod.DESCRICAO}
                                </td>
                                <td style={{ padding: "8px", color: "#666" }}>
                                  {prod.opcional || "-"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  R{prod.tabela_preco}
                                </td>
                                <td
                                  style={{ padding: "8px", textAlign: "right" }}
                                >
                                  {formatCurrency(prod.preco_item)}
                                </td>
                                {SIZES.map((s) => (
                                  <td
                                    key={s}
                                    style={{
                                      padding: "4px",
                                      textAlign: "center",
                                      fontWeight: gradeMap[s]
                                        ? "bold"
                                        : "normal",
                                      color: gradeMap[s] ? "#000" : "#e0e0e0",
                                      background: gradeMap[s]
                                        ? "#e8f5e9"
                                        : "transparent",
                                    }}
                                  >
                                    {gradeMap[s] || "-"}
                                  </td>
                                ))}
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {qtd}
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "var(--color-primary)",
                                  }}
                                >
                                  {formatCurrency(prod.subtotal)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={SIZES.length + 6}
                              style={{
                                textAlign: "center",
                                padding: "20px",
                                color: "#888",
                              }}
                            >
                              Nenhum item.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "obs" && (
                <div className={styles.tabContent}>
                  <textarea
                    className={styles.formInput}
                    value={pedido.obs || ""}
                    readOnly
                    rows={10}
                    style={{
                      width: "100%",
                      fontFamily: "inherit",
                      lineHeight: "1.5",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Resumo */}
            <div className={`${styles.resumoContainer} ${awsStyles.resumoFix}`}>
              <div className={styles.resumoItem}>
                <p className={styles.resumoLabel}>Qtd. Total</p>
                <p className={styles.resumoValue}>{totalItens}</p>
              </div>
              <div className={styles.resumoItem}>
                <p className={styles.resumoLabel}>Valor Total</p>
                <p className={styles.resumoValue}>
                  {formatCurrency(totalValor)}
                </p>
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button className={styles.cancelButton} onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AwsOrderDetailModal;
